import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  ArrowLeft,
  ArrowRight,
  Share2,
  Tag,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Zap,
  Clock3,
  ShieldCheck,
  Layers,
} from "lucide-react";
import useBlogs from "../../../hooks/useBlogs";
import { useTrackBlogView } from "../../../hooks/useBlogViews";
import LiveVisitorCounter from "../../../components/LiveVisitorCounter";
const BRAND = "#355931";
const BRAND_DEEP = "#253d27";
const SOFT_BG = "#F4F7F1";



// ─── FAQ Accordion ────────────────────────────────────────────────────────────
const FaqBlock = ({ block }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="my-8 space-y-3">
      {block.items.map((faq, i) => (
        <div
          key={i}
          className="border border-gray-200 rounded-xl overflow-hidden"
        >
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white hover:bg-[#F4F7F1] transition-colors duration-200"
          >
            <span className="text-sm sm:text-[15px] font-semibold text-[#253d27]">
              {faq.question}
            </span>
            <ChevronDown
              className="w-5 h-5 flex-shrink-0 transition-transform duration-300"
              style={{
                color: BRAND,
                transform: open === i ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>
          <div
            className="overflow-hidden transition-all duration-300"
            style={{ maxHeight: open === i ? "400px" : "0px" }}
          >
            <p className="px-5 py-4 text-sm sm:text-[15px] text-gray-600 leading-relaxed border-t border-gray-100 bg-white">
              {faq.answer}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Stats Highlight Cards (Image 2) ─────────────────────────────────────────
const STATS_ICONS = {
  currency: Zap,
  time: Clock3,
  cert: ShieldCheck,
  formats: Layers,
};

const StatsBlock = ({ block }) => (
  <div className="my-8">
    {block.intro && (
      <p className="text-[15px] text-gray-700 mb-4 leading-relaxed">
        {block.intro}
      </p>
    )}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 rounded-xl overflow-hidden border border-[#c9ddc4]">
      {block.items.map((item, i) => {
        const Icon = STATS_ICONS[item.icon] || Zap;
        return (
          <div
            key={i}
            className={`flex flex-col items-center text-center px-4 py-6 ${
              i !== block.items.length - 1
                ? "border-b sm:border-b-0 sm:border-r border-[#c9ddc4]"
                : ""
            }`}
            style={{ backgroundColor: SOFT_BG }}
          >
            <Icon className="mb-2 w-5 h-5" style={{ color: BRAND }} />
            <p
              className="text-lg sm:text-xl font-bold leading-tight mb-1"
              style={{ color: BRAND }}
            >
              {item.value}
            </p>
            <p className="text-xs sm:text-[13px] text-gray-600 leading-snug">
              {item.label}
            </p>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Comparison Table (Image 1) ───────────────────────────────────────────────
const ComparisonBlock = ({ block }) => (
  <div className="my-8">
    
    {/* ── Desktop: two-column table (md and above) ── */}
    <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th
              className="px-5 py-3.5 text-left text-[13px] font-bold w-1/2"
              style={{ backgroundColor: "#e8f0e5", color: BRAND_DEEP }}
            >
              {block.leftHeader}
            </th>
            <th
              className="px-5 py-3.5 text-left text-[13px] font-bold w-1/2"
              style={{ backgroundColor: "#d5e8d0", color: BRAND_DEEP }}
            >
              {block.rightHeader}
            </th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#fafcf9]"}>
              <td className="px-5 py-3 text-gray-700 border-t border-gray-100">
                <span className="flex items-start gap-2">
                  {row.leftOk ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND }} />
                  ) : (
                    <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
                  )}
                  <span>{row.left}</span>
                </span>
              </td>
              <td className="px-5 py-3 text-gray-700 border-t border-gray-100">
                <span className="flex items-start gap-2">
                  {row.rightOk ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND }} />
                  ) : (
                    <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
                  )}
                  <span>{row.right}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* ── Mobile: stacked cards (below md) ── */}
    <div className="md:hidden space-y-3">
      {block.rows.map((row, i) => (
        <div
          key={i}
          className="rounded-xl border border-gray-200 overflow-hidden shadow-sm"
        >
          {/* Left — In-House */}
          <div
            className="flex items-start gap-3 px-4 py-3 border-b border-gray-100"
            style={{ backgroundColor: "#f0f5ee" }}
          >
            <span
              className="text-[10px] font-bold uppercase tracking-wider mt-0.5 w-16 flex-shrink-0"
              style={{ color: BRAND }}
            >
              In-House
            </span>
            <span className="flex items-start gap-2 text-[13px] text-gray-700">
              {row.leftOk ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND }} />
              ) : (
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
              )}
              {row.left}
            </span>
          </div>
          {/* Right — Nutralike */}
          <div className="flex items-start gap-3 px-4 py-3 bg-white">
            <span
              className="text-[10px] font-bold uppercase tracking-wider mt-0.5 w-16 flex-shrink-0"
              style={{ color: BRAND }}
            >
              Nutralike
            </span>
            <span className="flex items-start gap-2 text-[13px] text-gray-700">
              {row.rightOk ? (
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: BRAND }} />
              ) : (
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400" />
              )}
              {row.right}
            </span>
          </div>
        </div>
      ))}
    </div>

  </div>
);

// ─── Render one content block ─────────────────────────────────────────────────
const ContentBlock = ({ block }) => {
  switch (block.type) {
// ✅ FIX — wrap in braces
case "heading": {
  const headingId = block.text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return (
    <h2
      id={headingId}
      className="mt-10 mb-4 text-xl sm:text-2xl font-bold leading-snug"
      style={{ color: BRAND_DEEP }}
    >
      {block.text}
    </h2>
  );
}
    case "paragraph":
      return (
        <p className="text-[15px] sm:text-base leading-relaxed text-gray-700 mb-5">
          {block.text}
        </p>
      );
    case "list":
      return (
        <ul className="mb-6 space-y-2 pl-1">
          {block.items.map((item, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-[15px] text-gray-700"
            >
              <span
                className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: BRAND }}
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "quote":
      return (
        <blockquote
          className="my-8 pl-5 pr-4 py-4 rounded-r-lg border-l-4 italic text-[15px] sm:text-base text-gray-700"
          style={{ borderColor: BRAND, backgroundColor: SOFT_BG }}
        >
          "{block.text}"
        </blockquote>
      );
    case "image":
      return (
        <figure className="my-8">
          <img
            src={block.src}
            alt={block.caption || ""}
            className="w-full rounded-lg object-cover"
            loading="lazy"
          />
          {block.caption && (
            <figcaption className="text-xs text-gray-500 text-center mt-2 italic">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    case "stats":
      return <StatsBlock block={block} />;
    case "comparison":
      return <ComparisonBlock block={block} />;
    case "faq":
      return <FaqBlock block={block} />;
    default:
      return null;
  }
};

// ─── Not Found ────────────────────────────────────────────────────────────────
const NotFound = () => (
  <section className="min-h-[60vh] bg-white flex items-center justify-center px-4">
    <div className="text-center max-w-md">
      <p
        className="text-xs tracking-[0.35em] font-semibold uppercase mb-3"
        style={{ color: BRAND }}
      >
        Blog · 404
      </p>
      <h1
        className="text-2xl sm:text-3xl font-bold mb-3"
        style={{ color: BRAND_DEEP }}
      >
        This article isn't here.
      </h1>
      <p className="text-gray-500 mb-6 text-sm sm:text-base">
        The post you're looking for may have been moved or renamed.
      </p>
      <Link
        to="/blog"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-semibold text-white transition-colors"
        style={{ backgroundColor: BRAND }}
      >
        <ArrowLeft size={16} />
        Back to Blog
      </Link>
    </div>
  </section>
);

// ─── Related card ─────────────────────────────────────────────────────────────
const RelatedCard = ({ blog }) => (
  <Link
    to={`/blog/${blog.slug}`}
    className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col"
  >
<div className="relative w-full h-56 overflow-hidden">
  <img
    src={blog.image}
    alt={blog.title}
    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
    loading="lazy"
  />
</div>
    <div className="p-4">
      <span
        className="text-[10px] tracking-widest font-bold uppercase"
        style={{ color: BRAND }}
      >
        {blog.category}
      </span>
      <h3
        className="mt-1 text-sm font-bold leading-snug line-clamp-2"
        style={{ color: BRAND_DEEP }}
      >
        {blog.title}
      </h3>
      <span
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all"
        style={{ color: BRAND }}
      >
        Read <ArrowRight size={12} />
      </span>
    </div>
  </Link>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const BlogDetail = () => {
  const { slug } = useParams();
  const { blogs, loading, getBlogBySlug } = useBlogs();
  const blog = getBlogBySlug(slug);
  useTrackBlogView(blog?.slug);



  const [activeId, setActiveId] = useState("");
  const clickedRef = React.useRef(false);

  useEffect(() => {


    if (!blog) return; // ✅ guard here

    const headingElements = blog.content
      .filter((b) => b.type === "heading")
      .map((h) => {
        const id = h.text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        return document.getElementById(id);
      })
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        if (clickedRef.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: "-10% 0px -80% 0px" }
    );

    headingElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [blog]);

      if (loading) return (
  <div className="min-h-[60vh] flex items-center justify-center">
    <p className="text-[#355931] font-semibold">Loading article...</p>
  </div>
);

  if (!blog) return <NotFound />; // ✅ guard after hooks is fine now

  const related = blogs.filter((b) => b.slug !== blog.slug).slice(0, 3);
  // ... rest of JSX

  return (
    <article className="bg-white">

      {/* HERO */}
      <section
        className="relative w-full overflow-hidden mb-24"
        style={{ backgroundColor: SOFT_BG }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-16 md:pt-14 md:pb-20">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs sm:text-[13px] text-gray-500 mb-8">
            <Link to="/" className="hover:underline" style={{ color: BRAND }}>
              Home
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:underline" style={{ color: BRAND }}>
              Blog
            </Link>
            <span>/</span>
            <span className="text-gray-400 truncate">{blog.category}</span>
          </nav>

          {/* Category eyebrow */}
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8" style={{ backgroundColor: BRAND }} />
            <span
              className="text-[11px] sm:text-xs tracking-[0.35em] font-bold uppercase"
              style={{ color: BRAND }}
            >
              {blog.category}
            </span>
          </div>

          {/* Title */}
          <h1
            className="text-2xl sm:text-4xl md:text-[42px] font-bold leading-[1.15] tracking-tight"
            style={{ color: BRAND_DEEP }}
          >
            {blog.title}
          </h1>

          {/* Excerpt */}
          <p className="mt-5 text-base sm:text-lg text-gray-600 leading-relaxed max-w-3xl">
            {blog.excerpt}
          </p>

          {/* Meta row */}
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-600 pt-5 border-t border-black/10">
            <span className="flex items-center gap-2">
              <span
                className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                style={{ backgroundColor: BRAND }}
              >
                {(blog.author || "N").slice(0, 1).toUpperCase()}
              </span>
              <span className="flex flex-col leading-tight">
                <span
                  className="text-[11px] tracking-wider uppercase"
                  style={{ color: BRAND }}
                >
                  Author
                </span>
                <span
                  className="font-semibold text-[13px]"
                  style={{ color: BRAND_DEEP }}
                >
                  {blog.author}
                </span>
              </span>
            </span>
              <span className="flex items-center gap-2 text-gray-600">
              <Calendar size={15} style={{ color: BRAND }} />
              {blog.date}
            </span>

            <LiveVisitorCounter slug={blog.slug} />

            {/* <span className="flex items-center gap-2 text-gray-600">
              <Clock size={15} style={{ color: BRAND }} />
              {blog.readTime}
            </span> */}
          </div>
        </div>
      </section>


            {/* COVER IMAGE */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-10 md:-mt-14 mb-12 md:mb-16">
              <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(0,0,0,0.15)]">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-80 md:h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

      {/* CONTENT */}
      
     <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 mt-8 md:mt-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-10">
          {/* Article body */}
          <div>
            {blog.content.map((block, i) => (
              <ContentBlock key={i} block={block} />
            ))}

            {/* Tags */}
            {blog.tags?.length > 0 && (
              <div className="mt-10 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag size={15} style={{ color: BRAND }} />
                  {blog.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-medium px-3 py-1.5 rounded-full"
                      style={{ color: BRAND, backgroundColor: SOFT_BG }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bottom nav */}
            <div className="mt-10 pt-8 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 font-semibold text-sm hover:gap-3 transition-all"
                style={{ color: BRAND }}
              >
                <ArrowLeft size={16} />
                All Articles
              </Link>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: blog.title, url: window.location.href });
                  } else {
                    navigator.clipboard?.writeText(window.location.href);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white transition-transform active:scale-95"
                style={{ backgroundColor: BRAND }}
              >
                <Share2 size={14} />
                Share
              </button>
            </div>
          </div>

{/* Sidebar TOC */}
<aside className="hidden lg:block">
  <div
    className="sticky top-24 rounded-xl p-5 border"
    style={{ backgroundColor: SOFT_BG, borderColor: "#DDE7D8" }}
  >
    <p
      className="text-[10px] tracking-[0.3em] font-bold uppercase mb-3"
      style={{ color: BRAND }}
    >
      On this page
    </p>
    <ul className="space-y-2.5">
      {blog.content
        .filter((b) => b.type === "heading")
        .map((h, i) => {
          const headingId = h.text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
          const isActive = activeId === headingId;
          return (
            <li key={i}>
              <a
                href={`#${headingId}`}
                  onClick={() => {
                    // Set active immediately on click and pause observer briefly
                    setActiveId(headingId);
                    clickedRef.current = true;
                    setTimeout(() => {
                      clickedRef.current = false;
                    }, 1000);
                  }}
                  className="text-[13px] leading-snug block transition-all duration-200 py-1 px-2 rounded-md"
                  style={{
                    color: isActive ? BRAND : "#4b5563",
                    backgroundColor: isActive ? "#e8f0e5" : "transparent",
                    fontWeight: isActive ? "600" : "400",
                    borderLeft: isActive ? `3px solid ${BRAND}` : "3px solid transparent",
                  }}
                >
                  {h.text}
                </a>
            </li>
          );
        })}
    </ul>
  </div>
</aside>
        </div>
      </section>

      {/* RELATED ARTICLES */}
      {related.length > 0 && (
        <section className="bg-[#eef1ec] py-14 mt-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <div>
                <p
                  className="text-[11px] tracking-[0.35em] font-bold uppercase mb-2"
                  style={{ color: BRAND }}
                >
                  Keep Reading
                </p>
                <h2
                  className="text-2xl sm:text-3xl font-bold"
                  style={{ color: BRAND_DEEP }}
                >
                  More from our journal
                </h2>
              </div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 font-semibold text-sm hover:gap-3 transition-all"
                style={{ color: BRAND }}
              >
                View all
                <ArrowRight size={15} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((r) => (
                <RelatedCard key={r.id} blog={r} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default BlogDetail;